/**
 * Map cumulative journey JSON (from getJourney) to proposal tab / section progress.
 * Backend stores one cumulative key per proposal screen: Stage 8.0 … 8.5 ProposalFormScreen*.
 */

import { PROPOSAL_UI_TABS, PROPOSAL_STAGE_BY_STEP } from '../../api/journeyService';

/** @type {Map<string, string>} journeyStage string → stepId */
const JOURNEY_STAGE_TO_STEP_ID = new Map();
for (const tab of PROPOSAL_UI_TABS) {
  for (const sec of tab.sections) {
    JOURNEY_STAGE_TO_STEP_ID.set(sec.journeyStage, sec.stepId);
  }
}

const STEP_IDS_ORDERED = PROPOSAL_UI_TABS.flatMap((t) => t.sections.map((s) => s.stepId));

/**
 * Infer proposal stepId from a cumulative journey object key (exact or fuzzy).
 * @param {string} cumulativeKey
 * @returns {string|null}
 */
export function cumulativeKeyToProposalStepId(cumulativeKey) {
  if (cumulativeKey == null) return null;
  const k = String(cumulativeKey).trim();
  if (JOURNEY_STAGE_TO_STEP_ID.has(k)) {
    return JOURNEY_STAGE_TO_STEP_ID.get(k);
  }
  const lower = k.toLowerCase();
  if (!lower.includes('proposal') && !/8\.\d/.test(lower)) return null;
  if (lower.includes('8.0')) return 'lifeAssured-personal';
  if (lower.includes('8.1')) return 'lifeAssured-bank';
  if (lower.includes('8.2')) return 'lifeAssured-additional';
  if (lower.includes('8.3')) return 'nominee';
  if (lower.includes('8.4')) return 'medical-lifestyle';
  if (lower.includes('8.5')) return 'medical-medical';
  return null;
}

/**
 * Build Redux `proposalData` shape from parsed cumulative stages (for resume/hydration).
 * @param {Record<string, unknown>|null} stagesObject
 * @returns {Record<string, unknown>|null}
 */
export function buildProposalProgressFromCumulative(stagesObject) {
  if (!stagesObject || typeof stagesObject !== 'object') return null;
  const progress = {
    hydratedFromJourney: true,
    lastUpdatedAt: new Date().toISOString(),
  };
  let any = false;
  for (const key of Object.keys(stagesObject)) {
    const stepId = cumulativeKeyToProposalStepId(key);
    if (stepId) {
      progress[stepId] = true;
      any = true;
    }
  }
  return any ? progress : null;
}

/**
 * Group cumulative stage payloads by top tab (lifeAssured | nominee | medical) for UI/debug.
 * Values are raw blocks from cumulative JSON (may be nested wrapper objects).
 * @param {Record<string, unknown>|null} stagesObject
 * @returns {{ lifeAssured: Record<string, unknown>, nominee: Record<string, unknown>, medical: Record<string, unknown> }}
 */
export function extractProposalStagesByTopTab(stagesObject) {
  const out = {
    lifeAssured: {},
    nominee: {},
    medical: {},
  };
  if (!stagesObject || typeof stagesObject !== 'object') return out;

  for (const tab of PROPOSAL_UI_TABS) {
    for (const sec of tab.sections) {
      const block = findStageBlock(stagesObject, sec.journeyStage);
      if (block != null) {
        out[tab.id][sec.stepId] = unwrapStagePayload(block);
      }
    }
  }
  return out;
}

/**
 * Ordered list of proposal step ids (Life Assured sections → Nominee → Medical sections).
 */
export function getProposalStepIdsOrdered() {
  return [...STEP_IDS_ORDERED];
}

/**
 * Reverse lookup: stepId → backend cumulative key label.
 * @param {string} stepId
 * @returns {string|undefined}
 */
export function proposalStepIdToJourneyStage(stepId) {
  return PROPOSAL_STAGE_BY_STEP[stepId];
}

function findStageBlock(stagesObject, journeyStage) {
  if (stagesObject[journeyStage] != null) {
    return stagesObject[journeyStage];
  }
  const keys = Object.keys(stagesObject);
  const needle = journeyStage.toLowerCase();
  const hit = keys.find((k) => k.toLowerCase() === needle);
  if (hit) return stagesObject[hit];
  const num = journeyStage.match(/8\.\d/);
  if (num) {
    const fuzzy = keys.find((k) => k.includes(num[0]) && /proposal/i.test(k));
    if (fuzzy) return stagesObject[fuzzy];
  }
  return null;
}

function unwrapStagePayload(block) {
  if (block == null) return null;
  if (typeof block === 'string') {
    try {
      return JSON.parse(block);
    } catch {
      return { _raw: block };
    }
  }
  if (typeof block === 'object' && !Array.isArray(block)) {
    const keys = Object.keys(block);
    if (keys.length === 1 && keys[0].startsWith('Stage ')) {
      return block[keys[0]];
    }
  }
  return block;
}
